import type { ApiError } from '@omnia/shared';
import { tr } from './i18n.ts';

/**
 * Empty in the browser (same-origin `/api`, session in an HttpOnly cookie).
 * Native shells (Tauri desktop / iOS) set VITE_API_URL and authenticate with a bearer token.
 */
export const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const usesBearer = API_URL !== '';
/** Tauri desktop / iOS shells: no shared browser session, so popup-based flows cannot work. */
export const IS_NATIVE_SHELL = usesBearer;
const REQUEST_TIMEOUT_MS = 20_000;

// Held in memory only: a bearer token in web storage would be readable by any injected script.
let bearerToken: string | null = null;

export function setSessionToken(token: string | null) {
  if (usesBearer) bearerToken = token;
}

export class RequestError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export function authHeaders(): Record<string, string> {
  return bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {};
}

export function apiUrl(path: string) {
  return `${API_URL}${path}`;
}

export async function toRequestError(response: Response): Promise<RequestError> {
  const body = (await response.json().catch(() => null)) as ApiError | null;
  return new RequestError(response.status, body?.error ?? 'http_error', body?.message ?? tr.common.unknownError);
}

export async function api<T>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal; keepalive?: boolean } = {}): Promise<T> {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const signal = init.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: init.method ?? 'GET',
      credentials: usesBearer ? 'omit' : 'include',
      headers: {
        ...authHeaders(),
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      signal,
      keepalive: init.keepalive,
    });
  } catch (error) {
    if (init.signal?.aborted) throw error;
    throw new RequestError(0, 'network', tr.common.offline);
  }
  if (!response.ok) throw await toRequestError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Retry only transient failures; client errors are deterministic. */
export function shouldRetry(failureCount: number, error: unknown) {
  if (failureCount >= 2) return false;
  return !(error instanceof RequestError) || error.status === 0 || error.status >= 500;
}

export function errorMessage(error: unknown): string {
  return error instanceof RequestError ? error.message : tr.common.unknownError;
}
